import React, { FC, Suspense } from 'react'
import { Hr, Loading, TitleController } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { DataLoader } from './TerminateContractDataLoader'
import { useContractNotificationCategories } from './hooks/useContractNotificationCategories'
import { Form } from './Form'
import { TerminationDisallowedBanner } from './components/TerminationDisallowedBanner/TerminationDisallowedBanner'
import { resolveContractTerminationTerminology } from './utils/resolveContractTerminationTerminology'

import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import {
  Breakpoint,
  ResidentNotifications,
  SidebarInfoSection,
  SidebarInfoSectionText,
  View,
} from 'components'

export type TerminateContractViewState = 'form' | 'preview' | 'success'

export const TerminateContract: FC = () => {
  const [view, setView] = React.useState<TerminateContractViewState>('form')
  const [terminateTenants, setTerminateTenants] = React.useState<number[]>([])
  const { t } = useTranslation(['common', 'terminateContract', 'titles'])
  const { frontPagePath } = useContractPaths()
  const { contractNotificationCategories } = useContractNotificationCategories()
  const { contractModification } = useModuleConfig()

  return (
    <TitleController
      title={
        contractModification
          ? t('titles:contractModification')
          : t('titles:terminateContract')
      }
    >
      <DataLoader>
        {(data) => {
          const terminationDisallowed =
            data.contract.terminationDisallowedReason
          const terminology = resolveContractTerminationTerminology(
            data.contract.contractType
          )

          return (
            <View>
              <View.Header>
                <View.HeaderLink to={frontPagePath}>
                  {t('common:views.home')}
                </View.HeaderLink>

                <View.HeaderTitle>
                  {contractModification
                    ? t('terminateContract:editView.title')
                    : t(terminology.viewTitle)}
                </View.HeaderTitle>

                <Breakpoint show tablet phone>
                  <ResidentNotifications
                    categories={contractNotificationCategories}
                  />
                </Breakpoint>
              </View.Header>
              <View.Sidebar>
                <Breakpoint show desktop>
                  <ResidentNotifications
                    categories={contractNotificationCategories}
                  />
                </Breakpoint>

                <Breakpoint hide tablet desktop>
                  <TerminationDisallowedBanner
                    reason={terminationDisallowed}
                    contractType={data.contract.contractType}
                  />
                </Breakpoint>

                {view === 'form' && (
                  <SidebarInfoSection>
                    <SidebarInfoSectionText>
                      {contractModification
                        ? t(terminology.viewDescriptionNewFeatures)
                        : t(terminology.viewDescription)}
                    </SidebarInfoSectionText>
                  </SidebarInfoSection>
                )}
                <Hr className="my-4 tablet:hidden desktop:hidden" />
              </View.Sidebar>
              <View.Content>
                <Suspense fallback={<Loading variant="details" />}>
                  <Form
                    {...data}
                    view={view}
                    setView={setView}
                    terminateTenants={terminateTenants}
                    setTerminateTenants={setTerminateTenants}
                  />
                </Suspense>
              </View.Content>
            </View>
          )
        }}
      </DataLoader>
    </TitleController>
  )
}
